import React from 'react';

/**
 * BrainSyncLoader - animated loading screen with the brain logo.
 *
 * Three layered SVGs:
 *  1. A soft pulsing radial glow behind the logo (ambient warmth).
 *  2. The brain icon itself, gently floating up/down.
 *  3. A rotating ring of dashes around the brain to suggest "syncing".
 *
 * Pure CSS animations - no Motion / Framer dependency, so this can be
 * rendered the instant the bundle loads, before any heavier component
 * mounts.
 */
export default function BrainSyncLoader({ label }) {
    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center bg-[#FDFCF8] overflow-hidden relative"
            data-testid="brainsync-loader"
        >
            {/* Subtle background gradient - same as landing page */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(209,250,229,0.35) 0%, transparent 70%)',
                }}
            />

            {/* Logo + animated ring - sized at 80% of original (was w-32, now w-26-ish) */}
            <div className="relative w-[6.4rem] h-[6.4rem] flex items-center justify-center">
                {/* Pulsing glow */}
                <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-bs-pulse" />

                {/* Rotating dashed sync ring */}
                <svg
                    className="absolute inset-0 w-full h-full animate-bs-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                >
                    <circle
                        cx="50"
                        cy="50"
                        r="46"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray="6 14"
                        opacity="0.55"
                    />
                </svg>

                {/* Brain logo tile - uses the same /icon-192.png the
                    Dashboard header renders, so the loading state and
                    the live app share one visual identity. */}
                <div className="relative w-[3.25rem] h-[3.25rem] rounded-2xl overflow-hidden shadow-lg animate-bs-float">
                    <img src="/icon-192.png" alt="BrainSync" className="w-full h-full object-cover" />
                </div>
            </div>

            {/* Wordmark + status */}
            <div className="mt-8 text-center select-none">
                <p className="font-heading text-xl font-bold text-[#2E3A2F] tracking-tight">
                    BrainSync
                </p>
                <p className="text-xs text-gray-400 mt-1.5 tracking-wide animate-bs-fadeloop">
                    {label || 'Syncing your second brain…'}
                </p>
            </div>

            {/* Three dot row that pulses left → right */}
            <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bs-dot" style={{ animationDelay: '0s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bs-dot" style={{ animationDelay: '0.2s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bs-dot" style={{ animationDelay: '0.4s' }} />
            </div>

            <style>{`
                @keyframes bs-spin { to { transform: rotate(360deg); } }
                @keyframes bs-pulse {
                    0%, 100% { transform: scale(1); opacity: 0.55; }
                    50% { transform: scale(1.18); opacity: 0.25; }
                }
                @keyframes bs-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
                @keyframes bs-fadeloop {
                    0%, 100% { opacity: 0.55; }
                    50% { opacity: 1; }
                }
                @keyframes bs-dot {
                    0%, 60%, 100% { transform: scale(1); opacity: 0.4; }
                    30% { transform: scale(1.6); opacity: 1; }
                }
                .animate-bs-spin { animation: bs-spin 5s linear infinite; }
                .animate-bs-pulse { animation: bs-pulse 2.4s ease-in-out infinite; }
                .animate-bs-float { animation: bs-float 2.4s ease-in-out infinite; }
                .animate-bs-fadeloop { animation: bs-fadeloop 1.8s ease-in-out infinite; }
                .animate-bs-dot { animation: bs-dot 1.4s ease-in-out infinite; }
            `}</style>
        </div>
    );
}
